import { DigestConf } from "../logic/DigestConf";
import { initConnectadProbe } from "../../tools/connectad-probe";

// Perimetro del gate touchmove, dal piu' stretto al piu' largo. Si valuta in quest'ordine:
//   1. TOUCH_GATE_POST_IDS non vuoto -> solo su quei post, le aree vengono ignorate
//   2. TOUCH_GATE_AREAS non vuoto    -> solo su quelle aree
//   3. entrambe vuote                -> tutto il mobile
// Passare da un livello al successivo e' una riga sola.
//
// I post sono identificati dalla classe sul body, come fa isTargetPostId() in DigestConf.
// Piu' stabile del pathname: non cambia se lo slug dell'articolo viene riscritto.
const TOUCH_GATE_POST_IDS: string[] = ["postid-2480144"];

// Aree su cui il gate e' attivo, per misurare l'impatto sulle revenue confrontandole col
// resto del sito. Usate solo se TOUCH_GATE_POST_IDS e' vuoto.
const TOUCH_GATE_AREAS: string[] = [];

// Da quale target di cp_ads_config leggere l'area.
// '' = convenzione di DigestConf, cioe' il valore del target la cui chiave e' il mainSite
//      (su fanpage: 'scienze', 'napoli', ... — e' quella usata in passato per pubstack).
// Altrimenti il nome del target da leggere, es. 'area' -> 'innovazione', 'category'.
const TOUCH_GATE_AREA_KEY: string = "";

class doPrebidJS {
  private isInitialized: boolean = false;
  private logHeader: string = "[PREBID]";
  private adPrebidSlots: any[] = [];
  private executionQueue: Array<() => void> = [];
  private targetingMap: Record<string, any> = {};
  private digestInstance: DigestConf | null = null;
  private touchGate: Promise<void> | null = null;

  constructor() {
    // Il gate va armato subito, al caricamento dello script, NON alla prima chiamata di
    // doPrebid(): con lazyLoading attivo doPrebid viene invocato quando lo slot si avvicina
    // al viewport, cioe' dopo che l'utente ha gia' scrollato. Installando il listener solo
    // allora il primo touchmove e' gia' passato e si aspetterebbe un secondo scroll che puo'
    // non arrivare mai.
    this.armTouchGate();
    initConnectadProbe();
  }

  // DigestConf e' istanziato pigramente: il suo costruttore dereferenzia uaDetect() e
  // cp_ads_config.targets, e se lanciasse qui impedirebbe l'assegnazione di window.prebidjs
  // in fondo al file, lasciando lo SDK "caricato ma non disponibile" senza spiegazioni.
  private get digest(): DigestConf {
    if (!this.digestInstance) this.digestInstance = new DigestConf(window);
    return this.digestInstance;
  }

  // Precondizioni obbligatorie: senza queste il wrapper non puo' costruire un'asta valida,
  // quindi il caricamento va bloccato invece di degradare in silenzio.
  private missingConfig(w: any): string[] {
    const missing: string[] = [];
    if (typeof w?.uaDetect !== "function") missing.push("window.uaDetect()");
    const cfg = w?.cp_ads_config;
    if (!cfg) {
      missing.push("cp_ads_config");
      return missing;
    }
    if (!Array.isArray(cfg.targets) || !cfg.targets.length) missing.push("cp_ads_config.targets");
    if (!cfg.targets?.find?.((t: any) => t?.key === "site")?.value) missing.push("cp_ads_config.targets[key=site]");
    if (!cfg.prebidVendor?.version) missing.push("cp_ads_config.prebidVendor.version");
    return missing;
  }

  private blockedByConfig(w: any): boolean {
    const missing = this.missingConfig(w);
    if (!missing.length) return false;
    console.error(`${this.logHeader} configurazione incompleta: caricamento BLOCCATO. Manca: ${missing.join(", ")}`);
    return true;
  }

  private getPbjs() {
    return (window as any).pbjs;
  }

  private ensurePbjsStub(): void {
    const w = window as any;
    if (!w.pbjs) w.pbjs = { que: [] };
    else if (!Array.isArray(w.pbjs.que)) w.pbjs.que = [];
  }

  private get isPbjsReady() {
    const pbjs = this.getPbjs();
    return !!(pbjs && typeof pbjs.requestBids === 'function' && typeof pbjs.setConfig === 'function');
  }

  private scriptSrcFrom(w: any): string {
    const v = this.resolveVersion(w);
    return `https://assets.fanpage.it/ciaoprebid-js/${v}/prebid.js`;
  }

  processQueue() {
    while (this.executionQueue.length > 0) {
      const task = this.executionQueue.shift();
      if (typeof task === "function") task();
    }
  }

  init(wnd: any, callback?: () => void) {
    console.log(`${this.logHeader} Caricamento ciaoPrebid...`);

    if (this.blockedByConfig(wnd)) {
      callback?.();
      return;
    }

    if (this.isPbjsReady) {
      console.log(`${this.logHeader} pbjs già pronto`);
      this.isInitialized = true;
      callback?.();
      return;
    }

    const src = this.scriptSrcFrom(wnd);

    if ((window as any).__ciaoprebidLoading) {
      (window as any).__ciaoprebidLoading.then(() => {
        if (this.isPbjsReady) this.isInitialized = true;
        callback?.();
      });
      return;
    }

    (window as any).__ciaoprebidLoading = new Promise<void>((resolve) => {
      const finish = (ready: boolean) => {
        if (ready) {
          this.isInitialized = true;
          (window as any).__ciaoprebidVersion = this.resolveVersion(wnd);
        } else {
          console.warn(`${this.logHeader} continuo senza Prebid (timeout/errore)`);
        }
        resolve();
        callback?.();
      };

      const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
      if (existing) {
        this.waitForPbjs().then(finish);
        return;
      }

      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.onload = () => this.waitForPbjs().then(finish);
      script.onerror = () => {
        console.error(`${this.logHeader} Errore nel caricamento di prebid.js`);
        finish(false);
      };
      document.head.appendChild(script);
    });
  }

  // il tipo di ritorno e' esplicito di proposito: il chiamante fa .then() sul risultato,
  // quindi il ramo di blocco deve restituire una Promise, non un array
  preparePrebidSlots(slots: any): Promise<any[]> {
    if (this.blockedByConfig(window)) return Promise.resolve([]);
    return this.digest.preparePrebidSlots(slots);
  }

  loadPrebidAndExecute(callback: () => void) {
    console.log(`${this.logHeader} Caricamento ciaoPrebid...`);

    if (this.blockedByConfig(window)) {
      this.executionQueue.push(callback);
      this.processQueue();
      return;
    }

    if (this.isPbjsReady) {
      this.isInitialized = true;
      this.executionQueue.push(callback);
      this.processQueue();
      return;
    }

    const src = this.scriptSrcFrom(window);

    if ((window as any).__ciaoprebidLoading) {
      (window as any).__ciaoprebidLoading.then(() => {
        if (this.isPbjsReady) this.isInitialized = true;
        this.executionQueue.push(callback);
        this.processQueue();
      });
      return;
    }

    (window as any).__ciaoprebidLoading = new Promise<void>((resolve) => {
      const run = () => {
        if (this.isPbjsReady) this.isInitialized = true;
        this.executionQueue.push(callback);
        this.processQueue();
      };

      const finish = (ready: boolean) => {
        if (ready) (window as any).__ciaoprebidVersion = this.resolveVersion(window);
        else console.warn(`${this.logHeader} continuo senza Prebid (timeout/errore)`);
        resolve();
        run();
      };

      const existing = document.querySelector<HTMLScriptElement>(`script[src="${src}"]`);
      if (existing) {
        this.waitForPbjs().then(finish);
        return;
      }

      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.onload = () => this.waitForPbjs().then(finish);
      script.onerror = () => {
        console.error(`${this.logHeader} Errore nel caricamento di prebid.js`);
        finish(false);
      };
      document.head.appendChild(script);
    });
  }

  private waitForPbjs(retries = 60, interval = 100): Promise<boolean> {
    this.ensurePbjsStub();
    const w = window as any;

    return new Promise<boolean>((resolve) => {
      let settled = false;
      const done = (ok: boolean) => {
        if (settled) return;
        settled = true;
        resolve(ok);
      };

      try { w.pbjs.que.push(() => done(true)); } catch { /* no-op */ }

      const tick = () => {
        if (this.isPbjsReady) return done(true);
        if (retries-- <= 0) {
          console.error(`${this.logHeader} pbjs non disponibile dopo timeout`);
          return done(false);
        }
        setTimeout(tick, interval);
      };
      tick();
    });
  }

  // Area della pagina corrente, letta dai target di cp_ads_config
  private currentArea(w: any): string | null {
    try {
      const targets = w?.cp_ads_config?.targets;
      if (!Array.isArray(targets)) return null;
      const valueOf = (key: string) => targets.find((t: any) => t?.key === key)?.value;
      if (TOUCH_GATE_AREA_KEY) return valueOf(TOUCH_GATE_AREA_KEY) ?? null;
      const mainSite = valueOf("site");
      if (!mainSite) return null;
      return (valueOf(mainSite) ?? mainSite) || null;
    } catch {
      return null;
    }
  }

  // Il gate si applica solo su mobile
  private touchGateApplies(w: any): boolean {
    if (!this.isPhone(w)) return false;

    if (TOUCH_GATE_POST_IDS.length) {
      const body = w?.document?.body;
      const match = !!body && TOUCH_GATE_POST_IDS.some((c) => !!body.classList?.contains(c));
      console.log(`${this.logHeader} post ${TOUCH_GATE_POST_IDS.join("/")}: gate touchmove ${match ? "ATTIVO" : "non attivo"}`);
      return match;
    }

    if (!TOUCH_GATE_AREAS.length) return true;

    const area = this.currentArea(w);
    const match = !!area && TOUCH_GATE_AREAS.some((a) => a.toLowerCase() === String(area).toLowerCase());
    console.log(`${this.logHeader} area '${area ?? "n/d"}': gate touchmove ${match ? "ATTIVO" : "non attivo"}`);
    return match;
  }

  // true solo su telefono: su tablet e desktop il gate touchmove non si applica
  private isPhone(w: any): boolean {
    try {
      return !!w?.uaDetect?.().device?.phone;
    } catch {
      return false;
    }
  }

  // Arma il gate una volta sola, al caricamento. La promise resta in attesa del primo
  // touchmove: se questo avviene prima che doPrebid venga chiamato, la promise e' gia'
  // risolta e l'asta parte immediatamente. Listener passive per non rallentare lo scroll.
  private armTouchGate(): void {
    if (this.touchGate) return;
    this.touchGate = new Promise<void>((resolve) => {
      try {
        window.addEventListener(
          "touchmove",
          () => {
            console.log(`${this.logHeader} touchmove rilevato: Prebid sbloccato`);
            resolve();
          },
          { passive: true, once: true }
        );
      } catch {
        // se addEventListener non e' disponibile non si resta bloccati per sempre
        resolve();
      }
    });
  }

  private waitFirstTouchMove(): Promise<void> {
    this.armTouchGate();
    return this.touchGate as Promise<void>;
  }

  private isAdUnitArray(input: any): input is Array<{ code: string; bids: any[]; mediaTypes: any }> {
    return (
      Array.isArray(input) &&
      input.length > 0 &&
      typeof input[0]?.code === "string" &&
      Array.isArray(input[0]?.bids) &&
      typeof input[0]?.mediaTypes === "object"
    );
  }

  doPrebid(inputSlots: any, callback: (t: any) => void) {
    console.log(`${this.logHeader} Avvio Prebid...`);

    // diagnostica: cosa riceve doPrebid prima di qualsiasi elaborazione nostra
    try {
      const raw = JSON.parse(JSON.stringify(inputSlots));
      console.log(`${this.logHeader} doPrebid INPUT (${Array.isArray(raw) ? raw.length : 0} elementi), adUnitArray=${this.isAdUnitArray(inputSlots)}`, raw);
      if (Array.isArray(raw)) {
        raw.forEach((s: any) => console.log(`${this.logHeader} doPrebid INPUT ${s?.id}: chiavi [${Object.keys(s || {}).join(', ')}] devicesToLimRender=${JSON.stringify(s?.devicesToLimRender ?? null)}`));
      }
    } catch { /* no-op */ }

    const executeBids = async () => {
      const adUnits = this.isAdUnitArray(inputSlots) ? inputSlots : await this.preparePrebidSlots(inputSlots);
      const run = () => this.getBidsTargeting(adUnits, callback.bind(this));
      this.isInitialized ? run() : this.loadPrebidAndExecute(run);
    };

    // Su telefono tutto Prebid e' differito al primo touchmove: finche' l'utente non scrolla
    // non si scarica prebid.js, non si costruiscono ad unit e non parte nessuna asta.
    // ATTENZIONE: non c'e' fallback a tempo, quindi una pageview senza scroll non produce
    // alcuna richiesta di bid. Su tablet e desktop resta il comportamento a idle callback.
    if (this.touchGateApplies(window)) {
      console.log(`${this.logHeader} mobile: Prebid differito al primo touchmove`);
      this.waitFirstTouchMove().then(() => executeBids());
      return;
    }

    if ("requestIdleCallback" in window) {
      (window as any).requestIdleCallback(() => executeBids(), { timeout: 300 });
    } else {
      setTimeout(() => executeBids(), 150);
    }
  }

  getBidsTargeting(slots: any[], callback: (t: any) => void) {
    return new Promise((resolve) => {
      if (!this.isPbjsReady) {
        console.warn(`${this.logHeader} pbjs non pronto — salto asta, ritorno targeting vuoto`);
        const empty: any = {};
        try { callback?.(empty); } catch { /* no-op */ }
        return resolve(empty);
      }

      this.getPbjs().que.push(() => {
        const pbjs = this.getPbjs();

        pbjs.aliasBidder("pubmatic", "pubmatic_apex", { gvlid: 76 });

        pbjs.setConfig({
          priceGranularity: "dense",
          useBidCache: true,
          enableTIDs: true,
          targetingControls: {
            allowSendAllBidsTargetingKeys: ['BIDDER', 'AD_ID', 'PRICE_BUCKET', 'DEAL', 'UUID'],
            auctionKeyMaxChars: 1900
          },
          consentManagement: {
            gdpr: {
              cmpApi: "iab",
              timeout: 500,
              actionTimeout: 10000,
              defaultGdprScope: true,
            },
          },
          bidderTimeout: 3000,
          auctionTimeout: 3000,
          timeout: 3200,
          // ignoreBidderCacheKey: cacha su Rubicon anche i bid che portano un loro videoCacheKey.
          // Serve perche' la creativita' VAST in GAM non accetta macro nell'host, quindi il cache
          // deve essere lo stesso per tutti i bidder
          cache: { url: "https://prebid-server.rubiconproject.com/cache", ignoreBidderCacheKey: true },
        });

        pbjs.setConfig({
          currency: {
            adServerCurrency: "EUR",
            defaultRates: {
              EUR: { USD: 1.145, GBP: 0.87, CHF: 0.92 },
              USD: { EUR: 0.873, GBP: 0.74, CHF: 0.78 },
            },
          },
          userSync: {
            filterSettings: {
              iframe: { bidders: ["msft", "ix", "rubicon", "pubmatic", "taboola", "connectad", "seedtag", "ogury", "ttd", "improvedigital", "missena", "yobee", "ozone", "amx", "onetag", "equativ", "clickio"], filter: "include" },
              image: { bidders: ["freewheel-ssp"], filter: "exclude" },
            },
            userIds: [
              {
                name: "pubCommonId",
                storage: { type: "cookie", name: "_pubcid", expires: 365 },
              },
            ],
          },
        });

        pbjs.setConfig({
          userSync: {
            userIds: [{
              name: 'id5Id',
              params: {
                partner: 1727,
                externalModuleUrl: 'https://cdn.id5-sync.com/api/1.0/id5PrebidModule.js',
                pd: this.getCookie('apat') ? this.getCookie('apat') : '',
                abTesting: {
                  enabled: true,
                  controlGroupPct: 1
                },
                canCookieSync: true,
              },
              storage: {
                type: 'html5',
                name: 'id5id',
                expires: 90,
                refreshInSeconds: 7200
              }
            }],
            auctionDelay: 250
          }
        });

        pbjs.enableAnalytics({
          provider: 'id5Analytics',
          options: {
            partnerId: 1727
          }
        });


        pbjs.setConfig({
          bidderSequence: "fixed",
          auctionOptions: { secondaryBidders: ["yobee"], rejectInvalidMediaTypes: false },
        });

        pbjs.setBidderConfig({
          bidders: ["pubmatic_apex"],
          config: {
            schain: {
              validation: "relaxed",
              config: {
                ver: "1.0",
                complete: 1,
                nodes: [{ asi: "publicismedia.it", sid: "12346", hp: 1 }],
              },
            },
          },
        });

        pbjs.setBidderConfig({
          bidders: ["ozone"],
          config: {
            cache: { url: "https://ampc.ozpr.cloud/cache" },
          },
        });

        pbjs.setConfig({
          ozone: {
            oz_whitelist_adserver_keys: ['oz_dummy']
          }
        });


        pbjs.bidderSettings = {
          msft: { storageAllowed: true },
          ix: { storageAllowed: true },
          rubicon: { storageAllowed: true },
          pubmatic: { storageAllowed: true },
          taboola: { storageAllowed: true },
          ozone: { storageAllowed: true },
          amx: { storageAllowed: true },
          onetag: { storageAllowed: true },
          equativ: { storageAllowed: true },
        };

        this.trackTTR();

        const min4high = {
          display: { kv: "display", min: 3.0 },
          video: { kv: "video", min: 5.0 },
        };

        pbjs.setConfig({
          floors: {
            enforcement: { enforceJS: true },
            floorMin: 0.30,
            data: {
              currency: 'EUR',
              schema: { fields: ['mediaType', 'size'] },
              values: {
                'banner|*': 0.30,
                'video|*': 0.70,
                '*|*': 0.30
              }
            }
          }
        });

        pbjs.requestBids({
          adUnits: slots,
          bidsBackHandler: () => {
            console.log(`${this.logHeader} Offerte ricevute`);
            (this as any).adServerTargeting = pbjs.getAdserverTargeting();
            console.log(`[PREBID-ANAL] Risposte dei bidder salvate:`, (this as any).adServerTargeting);

            for (const adUnitCode in (this as any).adServerTargeting) {
              if (this.targetingMap[adUnitCode]) {
                let highest_cpm = 0.0;
                for (const bidder in this.targetingMap[adUnitCode]) {
                  const slotType = adUnitCode.includes("_video") ? "video" : "display";
                  const data = this.targetingMap[adUnitCode][bidder];
                  highest_cpm = parseFloat(`${highest_cpm}`) > parseFloat(`${data.cpm}`) ? highest_cpm : data.cpm;
                  (this as any).adServerTargeting[adUnitCode]["pbj_high_yield"] =
                    highest_cpm >= parseFloat(`${min4high[slotType].min}`) ? min4high[slotType].kv : "no";
                  console.log("[PREBID-ANAL] -------------------------------" + slotType + "-------------------------------");
                  console.log(`[PREBID-ANAL] hb_ttr_${bidder} --> raw ${data.rawTTR.toFixed(0)} passed ${data.ttr.toFixed(0)}`);
                  console.log(`[PREBID-ANAL] high_yield = ${(this as any).adServerTargeting[adUnitCode]["pbj_high_yield"]} highest_cpm ${this.targetingMap[adUnitCode]["cpm"]}`);
                  (this as any).adServerTargeting[adUnitCode][`hb_ttr_${bidder}`] = data.ttr.toFixed(0);
                }
              }
            }

            slots.forEach((s: any) => {
              const code = s.code;
              const hasBids = this.targetingMap[code] && Object.keys(this.targetingMap[code]).length > 0;
              if (!hasBids) {
                if (!(this as any).adServerTargeting[code]) (this as any).adServerTargeting[code] = {};
                (this as any).adServerTargeting[code]["pbj_high_yield"] = "omg";
                console.log(`[PREBID-ANAL] high_yield = omg (no bids) for ${code}`);
              }
            });

            if (typeof callback === "function") callback((this as any).adServerTargeting);
            slots.forEach((s: any) => pbjs.removeAdUnit(s.code));
            (resolve as any)((this as any).adServerTargeting);
          },
        });
      });
    });
  }


  trackTTR() {
    const requestStartTimes: Record<string, number> = {};
    const pbjs = this.getPbjs();

    pbjs.onEvent("auctionInit", (event: any) => {
      requestStartTimes[event.auctionId] = Date.now();
    });

    pbjs.onEvent("bidResponse", (event: any) => {
      const auctionId = event.auctionId;
      const rawTTR = requestStartTimes[auctionId] ? Date.now() - requestStartTimes[auctionId] : 0;
      let ttr: number;
      if (rawTTR <= 1000) {
        ttr = Math.ceil(rawTTR / 50) * 50;
      } else {
        ttr = Math.ceil((rawTTR - 1000) / 200) * 200 + 1000;
      }
      ttr = Math.min(ttr, 3000);

      if (!this.targetingMap[event.adUnitCode]) this.targetingMap[event.adUnitCode] = {};
      this.targetingMap[event.adUnitCode][event.bidder] = {
        cpm: event.cpm,
        ttr: ttr,
        rawTTR: rawTTR,
      };
    });
  }

  private resolveVersion(w: any): string {
    const v = w?.cp_ads_config?.prebidVendor?.version;
    if (!v) {
      console.error(`${this.logHeader} versione Prebid mancante (cp_ads_config.prebidVendor.version)`);
      throw new Error(`${this.logHeader} versione Prebid mancante (cp_ads_config.prebidVendor.version)`);
    }
    return v;
  }

  private getCookie(name: string): string | null {
    return document.cookie
      .split('; ')
      .find(c => c.startsWith(name + '='))
      ?.split('=')[1]
      ?? null;
  }

}

(window as any).prebidjs = new doPrebidJS();
